import { Button, DialogBody, DialogFooter } from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Map as CivilDrawMap, Project } from '~/common/types';
import { deleteMapBoundsInLocalStorage } from '~/components2/Map/helpers/mapBounds';
import store from '../store';

interface Props {
  map: CivilDrawMap;
  closeDialog: () => void;
}

export default function DeleteMapDialog({ map, closeDialog }: Props) {
  const { projectId, currentMapId, project } = useSnapshot(store) as typeof store;
  const navigate = useNavigate();

  return (
    <>
      <DialogBody className="text-sm">
        Are you sure you want to delete <strong>{map.name}</strong>?
        <br />
        This cannot be undone.
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={closeDialog} autoFocus data-testid="civildraw-delete-map-cancel">
          Cancel
        </Button>
        <Button
          variant={'danger'}
          data-testid="civildraw-delete-map-confirm"
          onClick={async () => {
            closeDialog();
            await store.deleteMap(map.id);
            if (currentMapId === map.id) {
              // if deleting the current map, push to the project details page.
              navigate({ to: '/projects/$projectId', params: { projectId } });
            }

            // Delete the map bounds from local storage
            deleteMapBoundsInLocalStorage(currentMapId);

            const analyticsProject = project as unknown as Project;
            captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawMapDeleted, {
              project_id: analyticsProject.id,
              organization_id: analyticsProject.organization || undefined,
              placement: 'my maps',
              map_id: map.id,
            });
          }}
        >
          Delete
        </Button>
      </DialogFooter>
    </>
  );
}
